import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-hot-toast";
import API from "../../Network/API";
import Agent from "../../Network/Agent";
import CountdownTimer from "../../Props/CountdownTimer";
import AlarmAcknowledgmentViewModal from "../../Models/AlarmAcknowledgmentViewModal";
import LoadStatus from "../../Component/Rms/home/LoadStatus";
import Availability from "../../Component/Rms/home/Availability";
import useReportForm from "../report/hook/useReportForm";
import useReportExport from "../report/hook/useReportExport";
import "./TenantDashboard.css";

const KPI_CARDS = [
    { key: "total_site", title: "Total Site", icon: "fa-regular fa-map", tone: "slate", alert: false, alarmType: null },
    { key: "mains_fail", title: "Mains Fail", icon: "fa-regular fa-transformer-bolt", tone: "red", alert: true, alarmType: 1 },
    { key: "dc_low", title: "DC Low", icon: "fa-light fa-battery-quarter", tone: "blue", alert: true, alarmType: 2 },
    { key: "site_down", title: "Site Down", icon: "fa-regular fa-chart-line-down", tone: "orange", alert: true, alarmType: 26 },
    { key: "no_load", title: "No Load", icon: "fa-regular fa-chart-line-down", tone: "amber", alert: true, alarmType: "no_load" },
    { key: "overload", title: "Overload", icon: "fa-regular fa-chart-line-up", tone: "pink", alert: true, alarmType: 25 },
    { key: "door_open", title: "Door Open", icon: "fa-regular fa-door-open", tone: "cyan", alert: true, alarmType: 12 },
];

const selectStyles = {
    control: (base) => ({ ...base, minHeight: 38, borderColor: "#e2e8f0", boxShadow: "none" }),
    valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function splitAlarmTime(start) {
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) {
        return { time: "--", day: "", year: "", dateGreen: "" };
    }
    const month = d.toLocaleDateString("en-US", { month: "short" });
    const dayNum = String(d.getDate()).padStart(2, "0");
    return {
        time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        day: d.toLocaleDateString("en-GB", { day: "numeric", month: "long" }),
        year: String(d.getFullYear()),
        dateGreen: `${month},${dayNum} ${d.getFullYear()}`,
    };
}

function alarmIcon(type) {
    switch (Number(type)) {
        case 1: return "fa-regular fa-transformer-bolt";
        case 2: return "fa-light fa-battery-quarter";
        case 12: return "fa-regular fa-door-open";
        case 25: return "fa-regular fa-chart-line-up";
        case 26: return "fa-regular fa-chart-line-down";
        default: return "fa-light fa-bell";
    }
}

function alarmSeverity(type) {
    return [1, 12, 25, 26].includes(Number(type)) ? "high" : "mid";
}

export default function TenantDashboard() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.profileReducer);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({
        tenant_name: "",
        total_site: 0,
        mains_fail: 0,
        dc_low: 0,
        site_down: 0,
        no_load: 0,
        overload: 0,
        door_open: 0,
        alarms: [],
        no_load_sites: [],
    });
    const [exportingAlarms, setExportingAlarms] = useState(false);
    const [viewAlarm, setViewAlarm] = useState(null);
    const [alarmFilter, setAlarmFilter] = useState(null);
    const [alarmQuery, setAlarmQuery] = useState("");
    const [showSearch, setShowSearch] = useState(false);
    const [graphTab, setGraphTab] = useState("load");
    const [showNoLoad, setShowNoLoad] = useState(false);

    const {
        form,
        options,
        handleClusterChange,
        handleZoneChange,
        handleSiteChange,
        handleSiteSearch,
        handleAlarmsChange,
        resetForm,
        setForm,
    } = useReportForm(dispatch);

    const { progress, exportLoading, exportReport } = useReportExport();

    const fetchDashboard = () => {
        setLoading(true);
        API.get("/v1/oauth/tenant_dashboard")
            .then((res) => {
                setData(res.data?.data ?? {});
            })
            .catch(() => {
                toast.error("Failed to load tenant dashboard");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchDashboard();
    }, []);

    const exportAlarms = () => {
        if (exportingAlarms) return;
        setExportingAlarms(true);
        const toastId = toast.loading("Exporting alarms...");
        const payload = {};
        if (alarmFilter != null && alarmFilter !== "no_load") {
            payload.alarm_type = alarmFilter;
        }
        Agent.RMS_ALARM.export_active_alarm(payload)
            .then((response) => {
                const blob = new Blob([response.data], {
                    type: response.data?.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                if (blob.size < 100 && response.data?.type === "application/json") {
                    throw new Error("Export failed");
                }
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "rms_active_alarms.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Export downloaded", { id: toastId });
            })
            .catch(() => toast.error("Export failed", { id: toastId }))
            .finally(() => setExportingAlarms(false));
    };

    const alarms = data?.alarms ?? [];

    const filteredAlarms = useMemo(() => {
        const q = alarmQuery.trim().toLowerCase();
        return alarms.filter((row) => {
            if (alarmFilter != null && Number(row.alarm_type) !== Number(alarmFilter)) return false;
            if (!q) return true;
            return String(row.site_id || "").toLowerCase().includes(q)
                || String(row.name || "").toLowerCase().includes(q);
        });
    }, [alarms, alarmFilter, alarmQuery]);

    const onKpiClick = (card) => {
        if (card.key === "no_load") {
            setShowNoLoad(true);
            return;
        }
        if (card.alarmType == null) {
            setAlarmFilter(null);
            return;
        }
        setAlarmFilter((prev) => (prev === card.alarmType ? null : card.alarmType));
    };

    const filterTitle = KPI_CARDS.find((c) => c.alarmType === alarmFilter)?.title;

    return (
        <div className="tenant-dashboard">
            <div className="td-kpis">
                {KPI_CARDS.map((card) => {
                    const value = Number(data[card.key] ?? 0);
                    const isOn = card.alert && value > 0;
                    const selected = alarmFilter === card.alarmType && card.alarmType != null;
                    const className = `td-kpi tone-${card.tone}${isOn ? " is-on" : ""}${selected ? " is-selected" : ""}`;
                    const inner = (
                        <>
                            <span className="td-kpi-icon"><i className={card.icon} /></span>
                            <span className="td-kpi-meta">
                                <p className="label">{card.title}</p>
                                <p className="value">{loading ? "—" : value}</p>
                            </span>
                        </>
                    );
                    if (card.key === "total_site") {
                        return (
                            <Link key={card.key} to="/rms_stations" className={className}>
                                {inner}
                            </Link>
                        );
                    }
                    return (
                        <button type="button" key={card.key} className={className} onClick={() => onKpiClick(card)}>
                            {inner}
                        </button>
                    );
                })}
            </div>

            <div className="td-panels">
                <div className="td-card">
                    <div className="td-live-head">
                        <div className="td-live-title">
                            <i className="fa-solid fa-bell" />
                            <h6>Live Alarms ({filteredAlarms.length})</h6>
                        </div>
                        <div className="td-live-actions">
                            <button type="button" className="td-icon-btn" title="Search" onClick={() => setShowSearch((v) => !v)}>
                                <i className="fa-light fa-magnifying-glass" />
                            </button>
                            <button type="button" className="td-icon-btn" title="Refresh" onClick={fetchDashboard} disabled={loading}>
                                <i className={`fa-light fa-refresh ${loading ? "fa-spin" : ""}`} />
                            </button>
                            <button
                                type="button"
                                className="td-icon-btn"
                                title={exportingAlarms ? "Exporting..." : "Export"}
                                onClick={exportAlarms}
                                disabled={exportingAlarms}
                            >
                                <i className={`fa-light ${exportingAlarms ? "fa-spinner fa-spin" : "fa-file-export"}`} />
                            </button>
                        </div>
                    </div>
                    {showSearch && (
                        <div className="td-live-search">
                            <input
                                type="search"
                                className="form-control form-control-sm"
                                placeholder="Search site or alarm"
                                value={alarmQuery}
                                onChange={(e) => setAlarmQuery(e.target.value)}
                            />
                        </div>
                    )}
                    {filterTitle && (
                        <div className="td-live-filter">
                            Filtered by {filterTitle}
                            <button type="button" onClick={() => setAlarmFilter(null)}>Clear</button>
                        </div>
                    )}
                    <div className="td-live-wrap">
                        {!loading && filteredAlarms.map((row) => {
                            const stamp = splitAlarmTime(row.start);
                            const severity = alarmSeverity(row.alarm_type);
                            return (
                                <div key={row.id} className="td-live-item">
                                    <div className="td-live-time">
                                        <span className="t">{stamp.time}</span>
                                        <span>{stamp.day}</span>
                                        <span>{stamp.year}</span>
                                    </div>
                                    <div className="td-live-axis">
                                        <span className={`td-live-node ${severity}`}>
                                            <i className={alarmIcon(row.alarm_type)} />
                                        </span>
                                    </div>
                                    <div className={`td-live-bubble ${severity}`}>
                                        <div className="td-live-top">
                                            <Link
                                                to={`/site/${encodeURIComponent(row.site_id)}`}
                                                className="td-live-site"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {row.site_id}
                                            </Link>
                                            <span className="td-live-timer">
                                                <CountdownTimer targetDate={row.start} />
                                            </span>
                                        </div>
                                        <p className="td-live-name">{row.name || "-"}</p>
                                        <div className="td-live-foot">
                                            <span className="td-live-date">{stamp.dateGreen}</span>
                                            <button
                                                type="button"
                                                className="td-live-view"
                                                title="View"
                                                onClick={() => setViewAlarm(row)}
                                            >
                                                <i className="fa-light fa-eye" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {loading && <div className="text-center py-4 text-muted">Loading alarms...</div>}
                        {!loading && filteredAlarms.length === 0 && (
                            <div className="td-empty">
                                <i className="fa-light fa-bell-slash" />
                                No active alarms
                            </div>
                        )}
                    </div>
                </div>

                <div className="td-card">
                    <div className="td-card-head">
                        <div>
                            <h6>Graph</h6>
                            <span className="sub">Load status & availability</span>
                        </div>
                        <div className="td-graph-tabs">
                            <button type="button" className={graphTab === "load" ? "active" : ""} onClick={() => setGraphTab("load")}>
                                Load Status
                            </button>
                            <button type="button" className={graphTab === "availability" ? "active" : ""} onClick={() => setGraphTab("availability")}>
                                Availability
                            </button>
                        </div>
                    </div>
                    <div className="td-graph-body">
                        {graphTab === "load" ? (
                            <LoadStatus compact />
                        ) : (
                            <Availability lockedTenantId={user?.tenant_id} />
                        )}
                    </div>
                </div>

                <div className="td-card">
                    <div className="td-card-head">
                        <div>
                            <h6>Report</h6>
                            <span className="sub">Tenant filter is locked</span>
                        </div>
                    </div>
                    <div className="td-form">
                        <div className="mb-2">
                            <label>Report Type <em className="text-danger">*</em></label>
                            <Select
                                value={options.report_types.find((r) => r.value === form.report_type) || null}
                                onChange={(e) => setForm((prev) => ({ ...prev, report_type: e?.value }))}
                                options={options.report_types}
                                isClearable
                                placeholder="Select report"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div className="row g-2">
                            <div className="col-md-6 mb-2">
                                <label>Cluster</label>
                                <Select
                                    value={options.clusters.find((c) => c.value === form.cluster) || null}
                                    onChange={handleClusterChange}
                                    options={options.clusters}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label>Zone</label>
                                <Select
                                    value={options.zones.find((z) => z.value === form.zone) || null}
                                    onChange={handleZoneChange}
                                    options={options.zones}
                                    isClearable
                                    placeholder="All"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                        <div className="mb-2">
                            <label>SITE ID</label>
                            <Select
                                value={
                                    options.sites.find((s) => String(s.value) === String(form.site))
                                    || (form.site ? { value: form.site, label: form.site } : null)
                                }
                                onChange={handleSiteChange}
                                onInputChange={handleSiteSearch}
                                options={options.sites}
                                isClearable
                                filterOption={() => true}
                                placeholder="All"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        {form.report_type === 1 && (
                            <div className="mb-2">
                                <label>Alarm Type <em className="text-danger">*</em></label>
                                <Select
                                    isMulti
                                    value={form.rms_alarm}
                                    onChange={(e) => handleAlarmsChange("rms_alarm", e)}
                                    options={options.rms_alarms}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        )}
                        <div className="mb-2">
                            <label>Date range</label>
                            <div className="input-group">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={form.start_date}
                                    onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
                                />
                                <input
                                    type="date"
                                    className="form-control"
                                    value={form.end_date}
                                    onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="td-form-actions">
                            <button type="button" className="btn btn-success" onClick={() => (exportLoading ? null : exportReport(form))}>
                                <i className="fa-light fa-file-export me-1" />
                                {exportLoading ? "Exporting..." : "Export Report"}
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                                Reset
                            </button>
                        </div>
                        {exportLoading && (
                            <div className="progress td-progress mt-2">
                                <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${progress}%` }}>{progress}%</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showNoLoad && (
                <div className="td-modal-backdrop" onClick={() => setShowNoLoad(false)}>
                    <div className="td-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="td-modal-head">
                            <h6>No Load Sites ({(data.no_load_sites ?? []).length})</h6>
                            <button type="button" className="td-icon-btn" onClick={() => setShowNoLoad(false)}>
                                <i className="fa-light fa-xmark" />
                            </button>
                        </div>
                        <div className="td-modal-body">
                            {(data.no_load_sites ?? []).length === 0 ? (
                                <div className="td-empty">No no-load sites</div>
                            ) : (
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Site ID</th>
                                            <th>Customer Site</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.no_load_sites ?? []).map((row, i) => (
                                            <tr key={`${row.site_id}-${i}`}>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <Link to={`/site/${encodeURIComponent(row.site_id)}`} onClick={() => setShowNoLoad(false)}>
                                                        {row.site_id}
                                                    </Link>
                                                </td>
                                                <td>{row.customer_site_id || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {viewAlarm && (
                <AlarmAcknowledgmentViewModal
                    isOpen={!!viewAlarm}
                    onClose={() => setViewAlarm(null)}
                    alarm={viewAlarm}
                    type={1}
                />
            )}
        </div>
    );
}
